import { getAIProviderManager } from "./aiProviderFactory";
import { parseJsonFromText } from "./jsonUtils";
import { searchPerspectiveResources } from "./lessonPlanApi";
import { buildCombinedDocumentText } from "./documentTextExtractor";

const MAX_SOURCE_LENGTH = 18000;
const MAX_RESOURCE_COUNT = 8;
const MAX_VIDEO_COUNT = 6;
const DEFAULT_USER_LOCALE = "en-US";
const DEFAULT_USER_LANGUAGE_CODE = "en";

const normalizeText = (value = "") =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const truncateText = (value = "", maxChars = MAX_SOURCE_LENGTH) => {
  const normalized = String(value || "").trim();
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trim()}...`;
};

const normalizeStringArray = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => normalizeText(item))
    .filter(Boolean);

const normalizeLanguageCode = (value = "") => {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) return "";
  const match = normalized.match(/^[a-z]{2,3}(?:-[a-z0-9]{2,8})?/i);
  return match ? match[0].split("-")[0] : "";
};

const getUserLocale = () => {
  if (typeof navigator === "undefined") {
    return DEFAULT_USER_LOCALE;
  }
  return (
    normalizeText(navigator.language) ||
    normalizeText(Array.isArray(navigator.languages) ? navigator.languages[0] : "") ||
    DEFAULT_USER_LOCALE
  );
};

const getLanguageDisplayName = (
  languageCode = "",
  locale = DEFAULT_USER_LOCALE
) => {
  const normalizedCode = normalizeLanguageCode(languageCode);
  if (!normalizedCode) return "";
  try {
    return (
      new Intl.DisplayNames([locale], { type: "language" }).of(normalizedCode) ||
      normalizedCode
    );
  } catch (error) {
    return normalizedCode;
  }
};

const getResourceLanguageContext = () => {
  const locale = getUserLocale();
  const targetLanguageCode =
    normalizeLanguageCode(locale) || DEFAULT_USER_LANGUAGE_CODE;
  return {
    locale,
    targetLanguageCode,
    targetLanguageLabel:
      getLanguageDisplayName(targetLanguageCode, locale) || "English",
  };
};

const normalizePerspectiveList = (value, type = "represented") =>
  (Array.isArray(value) ? value : [])
    .map((item) => {
      if (typeof item === "string") {
        return type === "represented"
          ? { label: normalizeText(item), evidence: "", note: "" }
          : {
              label: normalizeText(item),
              whyItMatters: "",
              searchQuery: "",
              resourceFocus: "",
            };
      }

      const label = normalizeText(item?.label || item?.perspective || item?.group);
      if (!label) return null;

      if (type === "represented") {
        return {
          label,
          evidence: normalizeText(item?.evidence || item?.excerpt),
          note: normalizeText(item?.note || item?.whyIncluded),
        };
      }

      return {
        label,
        whyItMatters: normalizeText(
          item?.whyItMatters || item?.whyMissing || item?.importance
        ),
        searchQuery: normalizeText(
          item?.searchQuery || item?.suggestedSearchQuery
        ),
        resourceFocus: normalizeText(
          item?.resourceFocus || item?.suggestedResourceFocus || item?.counterNarrative
        ),
      };
    })
    .filter(Boolean);

const normalizeResourceList = (value) => {
  const seen = new Set();
  return (Array.isArray(value) ? value : [])
    .map((item) => {
      const title = normalizeText(item?.title);
      const url = normalizeText(item?.url || item?.link);
      if (!title || !url) return null;
      if (seen.has(url)) return null;
      seen.add(url);
      return {
        title,
        url,
        snippet: normalizeText(item?.snippet || item?.description),
        summary: normalizeText(item?.summary || item?.localizedSummary),
        query: normalizeText(item?.query),
        originalLanguageCode: normalizeLanguageCode(
          item?.originalLanguageCode || item?.languageCode
        ),
        originalLanguageLabel: normalizeText(item?.originalLanguageLabel),
      };
    })
    .filter(Boolean)
    .slice(0, MAX_RESOURCE_COUNT);
};

const normalizeVideoList = (value) => {
  const seen = new Set();
  return (Array.isArray(value) ? value : [])
    .map((item) => {
      const videoId = normalizeText(item?.videoId || item?.id);
      const url = normalizeText(
        item?.videoUrl ||
          item?.url ||
          (videoId ? `https://www.youtube.com/watch?v=${videoId}` : "")
      );
      const title = normalizeText(item?.title);
      if (!videoId || !url || !title) return null;
      if (seen.has(videoId)) return null;
      seen.add(videoId);
      return {
        videoId,
        title,
        url,
        thumbnailUrl: normalizeText(item?.thumbnailUrl),
        channelTitle: normalizeText(item?.channelTitle),
        publishedAt: normalizeText(item?.publishedAt),
        query: normalizeText(item?.query),
        snippet: normalizeText(item?.snippet || item?.description),
      };
    })
    .filter(Boolean)
    .slice(0, MAX_VIDEO_COUNT);
};

const buildPerspectiveSearchErrorMessage = (error) => {
  const code = String(error?.code || "").toLowerCase();
  const message = String(error?.message || "").trim();
  const normalizedMessage = message.toLowerCase();
  const isLocalhost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "::1", "[::1]"].includes(window.location.hostname);

  if (
    code.includes("internal") ||
    normalizedMessage === "internal" ||
    normalizedMessage.includes("cors") ||
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("load resource")
  ) {
    return isLocalhost
      ? "Supporting resources are unavailable right now. If you're testing locally, restart the dev server and make sure the deployed searchPerspectiveResources function is available."
      : "Supporting resources are unavailable right now. The missing-voices analysis still completed.";
  }

  return message || "Unable to search for supporting resources right now.";
};

export const buildResourceSearchQueries = (analysis = {}) => {
  const normalizedAnalysis = normalizePerspectiveAnalysis(analysis || {});
  const enrichedBaseQueries = new Set();
  const prioritizedQueries = normalizedAnalysis.missingPerspectives
    .map((item) => {
      const baseQuery = normalizeText(item.searchQuery);
      const enrichedQuery =
        baseQuery && item.resourceFocus
          ? normalizeText(`${baseQuery} ${item.resourceFocus}`)
          : baseQuery;
      if (baseQuery && enrichedQuery && enrichedQuery !== baseQuery) {
        enrichedBaseQueries.add(baseQuery);
      }
      return enrichedQuery;
    })
    .filter(Boolean);

  return Array.from(
    new Set(
      [
        ...prioritizedQueries,
        ...normalizeStringArray(normalizedAnalysis.searchQueries).filter(
          (query) => !enrichedBaseQueries.has(query)
        ),
      ].filter(Boolean)
    )
  ).slice(0, 4);
};

export const normalizePerspectiveAnalysis = (value) => {
  const representedPerspectives = normalizePerspectiveList(
    value?.representedPerspectives || value?.includedPerspectives,
    "represented"
  );
  const missingPerspectives = normalizePerspectiveList(
    value?.missingPerspectives,
    "missing"
  );
  const searchQueries = Array.from(
    new Set([
      ...normalizeStringArray(value?.searchQueries),
      ...missingPerspectives
        .map((item) => item.searchQuery)
        .filter(Boolean),
    ])
  ).slice(0, 4);

  return {
    summary: normalizeText(value?.summary),
    caution: normalizeText(value?.caution || value?.confidenceNote),
    representedPerspectives,
    missingPerspectives,
    searchQueries,
  };
};

const normalizeLocalizedResourceAnnotations = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => ({
      url: normalizeText(item?.url),
      localizedSummary: normalizeText(
        item?.localizedSummary || item?.summary || item?.description
      ),
      originalLanguageCode: normalizeLanguageCode(
        item?.originalLanguageCode || item?.languageCode
      ),
      relevance: normalizeText(item?.relevance || item?.status).toLowerCase(),
    }))
    .filter((item) => item.url);

const localizePerspectiveResources = async (resources = []) => {
  if (!resources.length) return resources;

  const languageContext = getResourceLanguageContext();
  const aiManager = getAIProviderManager();
  if (typeof aiManager?.generateStructuredJson !== "function") {
    return resources;
  }
  const prompt = `You are cleaning web search result cards for a classroom Missing Voices app.

User language: ${languageContext.targetLanguageLabel} (${languageContext.targetLanguageCode})

For each result:
- Decide whether it is relevant to the surfaced query.
- Mark customer-service, billing, account, telecom, login, shopping, and obviously unrelated pages as "irrelevant".
- Write a concise summary in ${languageContext.targetLanguageLabel}. Use one sentence and stay grounded in the title, snippet, query, and URL only.
- Detect the original language of the result text when reasonably clear. Return an ISO 639-1 code like "en", "es", "fr", or "ht". Return "" if unclear.

Return JSON only in this exact shape:
{
  "items": [
    {
      "url": "https://example.com",
      "relevance": "relevant",
      "localizedSummary": "short summary in the user's language",
      "originalLanguageCode": "en"
    }
  ]
}

Results:
${JSON.stringify(
    resources.slice(0, MAX_RESOURCE_COUNT).map((resource) => ({
      url: resource.url,
      title: resource.title,
      snippet: resource.snippet,
      query: resource.query,
    })),
    null,
    2
  )}`;

  try {
    const result = await aiManager.generateStructuredJson({
      prompt,
      metadata: {
        operation: "missing-perspective-resource-localization",
        llmSensitivity: "internal",
      },
      schemaHint:
        '{"items":[{"url":"https://example.com","relevance":"relevant","localizedSummary":"...","originalLanguageCode":"en"}]}',
    });

    const annotations = normalizeLocalizedResourceAnnotations(
      result?.parsed?.items || result?.parsed
    );

    if (!annotations.length) {
      return resources;
    }

    const annotationsByUrl = new Map(
      annotations.map((annotation) => [annotation.url, annotation])
    );

    return resources
      .map((resource) => {
        const annotation = annotationsByUrl.get(resource.url);
        if (!annotation) {
          return resource;
        }
        return {
          ...resource,
          summary: annotation.localizedSummary || resource.summary || resource.snippet,
          originalLanguageCode:
            annotation.originalLanguageCode || resource.originalLanguageCode,
          originalLanguageLabel:
            getLanguageDisplayName(
              annotation.originalLanguageCode || resource.originalLanguageCode,
              languageContext.locale
            ) || resource.originalLanguageLabel,
          summaryLanguageCode: languageContext.targetLanguageCode,
          relevance: annotation.relevance || "relevant",
        };
      })
      .filter((resource) => resource.relevance !== "irrelevant");
  } catch (error) {
    console.warn("Resource localization failed:", error);
    return resources;
  }
};

export const analyzeMissingPerspectives = async ({
  text = "",
  documents = [],
  sourceLabel = "source text",
} = {}) => {
  const combinedText = truncateText(
    buildCombinedDocumentText({ text, documents }),
    MAX_SOURCE_LENGTH
  );

  if (!combinedText) {
    throw new Error("Add text, a source URL, or uploaded documents before analyzing.");
  }

  const prompt = `Analyze the following ${sourceLabel} for whose voices are centered and whose voices are missing.

Goals:
- Identify whose voices, experiences, narratives, or social positions are clearly represented.
- Identify whose voices are absent, underrepresented, flattened, or treated as background.
- Stay grounded in the submitted text. Do not invent details the text does not support.
- If the text is too short or too generic, say so in a caution note.
- Suggest concrete web-search queries that would help a teacher or student bring in fuller context and missing voices.
- Make each search query specific enough to find archives, museums, universities, oral histories, primary sources, or credible explainers about the missing voices.
- Avoid vague queries that can be mistaken for browser/search-history help pages. Include the topic, the missing group or voice, and the kind of resource that would help.

Return JSON only in this exact shape:
{
  "summary": "short paragraph",
  "caution": "optional caution note",
  "representedPerspectives": [
    {
      "label": "group or perspective",
      "evidence": "brief evidence from the text",
      "note": "why this perspective is present"
    }
  ],
  "missingPerspectives": [
    {
      "label": "group or perspective",
      "whyItMatters": "why this absence matters",
      "searchQuery": "specific web query targeting a useful resource type",
      "resourceFocus": "what kind of resource would help"
    }
  ],
  "searchQueries": ["specific query 1", "specific query 2"]
}

${sourceLabel.toUpperCase()}:
${combinedText}`;

  const aiManager = getAIProviderManager();
  const result = await aiManager.generateStructuredJson({
    prompt,
    metadata: { operation: "missing-perspective-analysis" },
    schemaHint:
      '{"summary":"...","caution":"...","representedPerspectives":[{"label":"...","evidence":"...","note":"..."}],"missingPerspectives":[{"label":"...","whyItMatters":"...","searchQuery":"...","resourceFocus":"..."}],"searchQueries":["..."]}',
  });

  const parsed =
    result?.parsed || parseJsonFromText(result?.raw || result?.content || "");
  return normalizePerspectiveAnalysis(parsed);
};

export const runMissingPerspectiveWorkflow = async ({
  text = "",
  documents = [],
  sourceLabel = "source text",
} = {}) => {
  const analysis = await analyzeMissingPerspectives({
    text,
    documents,
    sourceLabel,
  });

  let resources = [];
  let videos = [];
  let searchError = "";
  const searchQueries = buildResourceSearchQueries(analysis);
  if (searchQueries.length > 0) {
    try {
      const result = await searchPerspectiveResources({
        queries: searchQueries,
      });
      resources = await localizePerspectiveResources(
        normalizeResourceList(result?.resources)
      );
      videos = normalizeVideoList(result?.videos);
    } catch (error) {
      console.error("Perspective resource search failed:", error);
      searchError = buildPerspectiveSearchErrorMessage(error);
    }
  }

  return {
    analysis,
    resources,
    videos,
    searchError,
  };
};

const formatPerspectiveBulletList = (items = [], formatter) =>
  items.length > 0
    ? items.map((item) => `- ${formatter(item)}`).join("\n")
    : "- None identified.";

export const buildPerspectiveRegenerationPrompt = ({
  lessonPlan = "",
  analysis,
  resources = [],
  videos = [],
} = {}) => {
  const normalizedAnalysis = normalizePerspectiveAnalysis(analysis || {});
  const resourceLines =
    resources.length > 0
      ? resources
          .slice(0, MAX_RESOURCE_COUNT)
          .map(
            (item) =>
              `- [${item.title}](${item.url})${item.query ? ` | surfaced for: ${item.query}` : ""}${item.summary || item.snippet ? ` | note: ${item.summary || item.snippet}` : ""}${item.originalLanguageLabel ? ` | original language: ${item.originalLanguageLabel}` : ""}`
          )
          .join("\n")
      : "- No external resources were available.";
  const videoLines =
    videos.length > 0
      ? videos
          .slice(0, MAX_VIDEO_COUNT)
          .map(
            (item) =>
              `- [${item.title}](${item.url})${item.channelTitle ? ` | channel: ${item.channelTitle}` : ""}${item.query ? ` | surfaced for: ${item.query}` : ""}`
          )
          .join("\n")
      : "- No video suggestions were available.";

  return `Revise the lesson plan below to address the identified missing voices while preserving the existing markdown heading structure.

Requirements:
- Keep the current heading hierarchy and return a complete markdown lesson plan.
- Preserve the core lesson objective unless the missing voices require a minor clarification.
- Strengthen the existing "VOICES ANALYSIS" section, or the equivalent section that reviews included and missing voices if the heading uses older wording.
- Integrate the missing voices into instruction, materials, guided practice, discussion, and assessment where it improves coherence.
- Use the external resources only when they genuinely deepen the lesson.
- Do not add meta-commentary about this prompt or about what you changed.

Perspective analysis summary:
${normalizedAnalysis.summary || "No summary provided."}

Centered voices:
${formatPerspectiveBulletList(
    normalizedAnalysis.representedPerspectives,
    (item) =>
      `${item.label}${item.evidence ? ` | evidence: ${item.evidence}` : ""}${
        item.note ? ` | note: ${item.note}` : ""
      }`
  )}

Missing voices to address:
${formatPerspectiveBulletList(
    normalizedAnalysis.missingPerspectives,
    (item) =>
      `${item.label}${item.whyItMatters ? ` | why it matters: ${item.whyItMatters}` : ""}${
        item.resourceFocus ? ` | resource focus: ${item.resourceFocus}` : ""
      }`
  )}

Helpful external resources:
${resourceLines}

Helpful video suggestions:
${videoLines}

Current lesson plan:
${String(lessonPlan || "").trim()}`;
};
